import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import ROUTES from '../../routes';
import api from './../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { formatDate, handleErrors } from '../../utils/common';

const STATUS_COLORS = { active: 'primary', paid: 'success', cancelled: 'secondary' };

const formatMoney = (value) => (value != null ? Number(value).toFixed(2) : '-');

function PaymentLinks() {
    const Swal = window.Swal;

    const limit = 10;
    const [links, setLinks] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchLinks = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const offset = (page - 1) * limit;
            const params = new URLSearchParams({ limit, offset });
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (status) params.set('status', status);

            const res = await api.get(`/payment-link/list?${params.toString()}`);
            if (res.status === 200) {
                setLinks(res.data.payment_links || []);
                const total = res.data.total || 0;
                setTotalPages(Math.ceil(total / limit) || 1);
            }
        } catch (err) {
            handleErrors(err);
        } finally {
            setLoading(false);
        }
    }, [limit, debouncedSearch, status]);

    useEffect(() => {
        fetchLinks(currentPage);
    }, [currentPage, fetchLinks]);

    const handleCopy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            showToast('success', 'Payment link copied to clipboard', 'Copied');
        } catch (err) {
            showToast('error', 'Could not copy the link. Copy it manually: ' + url, 'Error');
        }
    };

    const handleCancel = (linkId) => {
        Swal.fire({
            title: 'Cancel this payment link?',
            html: '<i class="fas fa-ban" style="font-size: 42px; color: #d33; margin-bottom: 10px;"></i><p>The customer will no longer be able to pay with it.</p>',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, cancel it!',
            cancelButtonText: 'No'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.patch(`/payment-link/${linkId}/cancel`);
                    if (res.status === 200) {
                        showToast('success', res.data.message, 'Success');
                        setLinks((prev) => prev.map((l) => (l.id === linkId ? { ...l, status: 'cancelled' } : l)));
                    }
                } catch (err) {
                    handleErrors(err);
                }
            }
        });
    };

    const generatePagination = () => {
        const pages = [];
        const maxVisible = 5;
        const sidePages = 2;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > sidePages + 2) pages.push('...');

            const start = Math.max(2, currentPage - sidePages);
            const end = Math.min(totalPages - 1, currentPage + sidePages);
            for (let i = start; i <= end; i++) pages.push(i);

            if (currentPage < totalPages - sidePages - 1) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.PAYMENT_LINKS}>Payment Links</Link>
                    </li>
                </ol>
            </div>
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0">Payment Links</h4>
                            <Link to={ROUTES.ADD_PAYMENT_LINK} className="btn btn-primary btn-sm">
                                <i className="fas fa-plus me-2"></i>Create Link
                            </Link>
                        </div>
                        <div className="card-body">
                            <div className="row d-flex justify-content-start">
                                <div className="col-md-4 mb-3">
                                    <div className="input-group search-area">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search product or customer..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <span className="input-group-text">
                                            <i className="flaticon-381-search-2"></i>
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <select
                                        className="form-control"
                                        value={status}
                                        onChange={(e) => {
                                            setStatus(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="">All statuses</option>
                                        <option value="active">Active</option>
                                        <option value="paid">Paid</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                            </div>
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th className="width80"><strong>#</strong></th>
                                                <th><strong>Product</strong></th>
                                                <th><strong>Qty</strong></th>
                                                <th><strong>Unit Price</strong></th>
                                                <th><strong>Total</strong></th>
                                                <th><strong>Customer</strong></th>
                                                <th><strong>Status</strong></th>
                                                <th><strong>Order</strong></th>
                                                <th><strong>Created At</strong></th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {links.length === 0 && (
                                                <tr>
                                                    <td colSpan={10} className="text-center">No payment links found</td>
                                                </tr>
                                            )}
                                            {links.map((link, index) => (
                                                <tr key={link.id}>
                                                    <td><strong>{(currentPage - 1) * limit + index + 1}</strong></td>
                                                    <td><span className="w-space-no">{link.title}</span></td>
                                                    <td>{link.qty}</td>
                                                    <td>{formatMoney(link.price)}</td>
                                                    <td>{formatMoney(link.total_amount)}</td>
                                                    <td>
                                                        {link.customer_name || link.customer_email || link.customer_phone ? (
                                                            <>
                                                                {link.customer_name && <div>{link.customer_name}</div>}
                                                                {link.customer_email && <div className="small">{link.customer_email}</div>}
                                                                {link.customer_phone && <div className="small">{link.customer_phone}</div>}
                                                            </>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-${STATUS_COLORS[link.status] || 'secondary'}`}>
                                                            {link.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {link.order ? (
                                                            <Link to={`/orders/${link.order.order_id}`}>{link.order.order_number}</Link>
                                                        ) : '-'}
                                                    </td>
                                                    <td>{formatDate(link.t)}</td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <button
                                                                type="button"
                                                                title="Copy link"
                                                                className="btn btn-primary shadow btn-xs sharp me-1"
                                                                onClick={() => handleCopy(link.url)}
                                                            >
                                                                <i className="fas fa-copy"></i>
                                                            </button>
                                                            {link.status === 'active' && (
                                                                <button
                                                                    type="button"
                                                                    title="Cancel link"
                                                                    className="btn btn-danger shadow btn-xs sharp"
                                                                    onClick={() => handleCancel(link.id)}
                                                                >
                                                                    <i className="fas fa-ban"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>

                                {generatePagination().map((page, index) =>
                                    page === '...' ? (
                                        <li className="page-item disabled" key={index}>
                                            <span className="page-link">...</span>
                                        </li>
                                    ) : (
                                        <li className={`page-item ${currentPage === page ? 'active' : ''}`} key={index}>
                                            <a className="page-link" href="#/" onClick={() => setCurrentPage(page)}>{page}</a>
                                        </li>
                                    )
                                )}

                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PaymentLinks;
